import type { Instance, Project, TierConfig } from '@nx/state';
import { NEO4J_MANAGED_KEY, TIER } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';

import {
  dseDefaults,
  enterpriseDefaults,
  gbStringToInt,
  gdsDefaults,
  getDefaultName,
  getValidSize,
  mteDefaults,
  proDefaults,
} from '../../entities/helpers';
import type { InstanceFormData } from '../../entities/model';

export const defaults = (
  instance: Instance,
  targetTier: TIER,
  tierConfig: TierConfig,
  project: Project,
): InstanceFormData => {
  const defaultName = getDefaultName(instance.name, 'Clone');

  let data: InstanceFormData;

  switch (targetTier) {
    case TIER.GDS:
      data = {
        ...gdsDefaults(tierConfig, [], project),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        vectorOptimized: instance.vectorOptimized,
      };
      break;
    case TIER.FREE:
    case TIER.PROFESSIONAL:
      data = {
        ...proDefaults(tierConfig, [], project, false),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        vectorOptimized: instance.vectorOptimized,
        gdsPlugin: instance.gdsPlugin,
      };
      break;
    case TIER.MTE:
      data = {
        ...mteDefaults(tierConfig, [], project),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        vectorOptimized: instance.vectorOptimized,
      };
      break;
    case TIER.AURA_DSE:
      data = {
        ...dseDefaults(tierConfig, project),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        encryptionKeyRef: instance.encryptionKeyRef ?? NEO4J_MANAGED_KEY,
        vectorOptimized: instance.vectorOptimized,
      };
      break;
    case TIER.ENTERPRISE:
      data = {
        ...enterpriseDefaults(tierConfig, project),
        name: defaultName,
        cloudProvider: instance.cloudProvider,
        region: instance.region,
        encryptionKeyRef: instance.encryptionKeyRef ?? NEO4J_MANAGED_KEY,
        vectorOptimized: instance.vectorOptimized,
      };
      break;
    default:
      throw new Error('Tier not supported');
  }
  const availableRegions = tierConfig.cloudProviderRegions[data.cloudProvider] ?? [];
  const region = (availableRegions.find((r) => r.name === data.region) ?? availableRegions[0])?.name;
  data = { ...data, migrateToBlock: true, region };
  const size = getValidSize(data, project, tierConfig.sizes, {
    resizeThreshold: isNotNullish(instance.desiredSettings.storage)
      ? gbStringToInt(instance.desiredSettings.storage)
      : undefined,
    isTrial: false,
    defaultSize: instance.desiredSettings,
  });
  return { ...data, size };
};
